"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FREE_ITEMS = [
  "8D Rotation with circular, sine, triangle & bounce patterns",
  "8 presets + sub-variants (Warm, Deep, Punchy, Intimate, Grand, Cathedral)",
  "Interaural Time Delay (0.7ms bio-accurate) & Head Shadow filtering",
  "Sub-bass 45 Hz, Bass shelf 80 Hz, Clarity 3.5 kHz, Air shimmer 10 kHz",
  "Convolution reverb with 2.5s synthesized impulse response",
  "LFO on audio thread \u2014 works even in background tabs",
  "Speed, 8D Movement, bass, clarity and reverb controls",
  "Auto DJ beat-aware transitions with an overlapping outgoing deck",
  "Real-time pan position visualizer (120\u00d7120 canvas)",
  "Strength slider blends variant presets from subtle to extreme",
  "30-second shareable video samples (1080p MP4 with visualizer)",
  "60-second free previews of every Pro mode",
  "100% client-side \u2014 zero data sent anywhere, ever",
];

const PRO_EXTRAS = [
  "Multi-band Surround: lows planted, mids orbit, highs shimmer (250 Hz / 2 kHz crossover)",
  "HRTF 3D: browser-native positioning with Orbit, Figure-8 & Drift paths",
  "Binaural Beats: Focus 15 Hz, Relax 10 Hz, Meditate 6 Hz, Sleep 3 Hz",
  "Full-length exports \u2014 record up to 10 minutes per take",
  "1080p MP4 video clips with live visualizer & preset label",
  "Lossless WAV (16-bit) + compressed M4A audio export",
  "Lifetime updates \u2014 every future Pro feature included",
];

const CHECK = (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function AllFeatures() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-fg]", {
        scrollTrigger: { trigger: ref.current, start: "top 80%", toggleActions: "play none none none" },
        y: 24,
        autoAlpha: 0,
        duration: 0.5,
        stagger: 0.12,
        ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="all-features" ref={ref} className="relative py-16 md:py-20 px-6">
      {/* Full-width tinted background stripe */}
      <div className="absolute inset-0 border-y" style={{ background: "rgba(255,255,255,0.012)", borderColor: "var(--border-subtle)" }} />

      <div className="relative max-w-[1200px] mx-auto">
        <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#FF9F1C] mb-3">
          Under the Hood
        </p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
          Everything packed in.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Free card */}
          <div
            data-fg
            className="rounded-lg p-6 sm:p-8"
            style={{ background: "var(--surface)", boxShadow: "var(--shadow-card)" }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#2EC4B6]">
                Free
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[1px] px-2 py-0.5 rounded-full" style={{ border: "1px solid rgba(46,196,182,0.2)", background: "rgba(46,196,182,0.1)", color: "#2EC4B6" }}>
                13 features
              </span>
            </div>
            <ul className="space-y-3">
              {FREE_ITEMS.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-[#b3b3b3] leading-snug">
                  <span className="text-[#2EC4B6] mt-0.5">{CHECK}</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro card */}
          <div
            data-fg
            className="rounded-lg p-6 sm:p-8 relative overflow-hidden"
            style={{ background: "var(--surface)", boxShadow: "var(--shadow-heavy)", border: "1px solid rgba(255,51,102,0.15)" }}
          >
            <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full blur-[60px] pointer-events-none" style={{ background: "rgba(255,51,102,0.08)" }} />

            <div className="relative">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#FF3366]">
                  Pro
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[1px] px-2 py-0.5 rounded-full text-white" style={{ background: "linear-gradient(to right, #FF3366, #FF9F1C)" }}>
                  $4.99 one-time
                </span>
              </div>

              <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#b3b3b3]/70 mb-3">
                Everything in Free
              </p>

              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-px" style={{ background: "rgba(255,51,102,0.15)" }} />
                <span className="text-[9px] font-bold uppercase tracking-[1.4px] text-[#FF3366]">Plus</span>
                <div className="flex-1 h-px" style={{ background: "rgba(255,51,102,0.15)" }} />
              </div>

              <ul className="space-y-3">
                {PRO_EXTRAS.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[13px] text-[#b3b3b3] leading-snug">
                    <span className="text-[#FF3366] mt-0.5">{CHECK}</span>
                    {item}
                  </li>
                ))}
              </ul>

              <a
                href="https://harshdev77.gumroad.com/l/inoxg?wanted=true"
                target="_blank"
                rel="noopener noreferrer"
                title="Buy the 8Dfy Pro license — $4.99 one-time, via Gumroad"
                className="flex items-center justify-center gap-2 mt-8 w-full py-3 rounded-full text-xs font-bold uppercase tracking-[1.8px] relative overflow-hidden transition-opacity duration-200 hover:opacity-90"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366] to-[#FF9F1C]" />
                <span className="relative text-white">Get Pro License &mdash; $4.99 <span aria-hidden="true">&#8599;</span></span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
